import React, {
    type ChangeEventHandler,
    type FocusEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import debounce from 'lodash.debounce';
import {WidthItem} from 'src/components/width-item';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {searchResources} from 'src/openapi/admin/apis/EntitlementsResourceSearchApi';
import {getRoleNamesByIds} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {searchEntitlements} from 'src/openapi/admin/apis/EntitlementsSearchApi';
import {RelationLevel} from 'src/openapi/admin/models/RelationLevel';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';

import {PresetSuggestResults} from './components';
import {
    MAX_RESULTS,
} from './constants';
import {
    mapRelationsToSuggestions,
    mapSelectedRelations,
} from './utils';

import {presetSuggestList} from './preset-suggest.module.styl';


import type {SuggestionItem, SuggestionSections} from './types';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';
import type {Relation} from 'src/openapi/admin/models/Relation';
import type {Role} from 'src/openapi/admin/models/Role';
import type {ServerResponse} from 'src/typedef';

type Props = {
    selectedRelations: PresetRelationItem[],
    onSearch: (relations: PresetRelationItem[]) => void,
};

export const PresetSuggest = ({selectedRelations, onSearch}: Props) => {
    const apiDispatch = useAppDispatch();
    const autosuggestId = 'preset-suggest';
    const listboxId = `${autosuggestId}-list`;

    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState<ServerResponse<SuggestionSections> | null>(null);

    const selectedSuggestions = useMemo(
        () => mapSelectedRelations(selectedRelations),
        [selectedRelations],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadSuggestedItems = useCallback(debounce(async (searchedQuery: string) => {
        const resourceName = searchedQuery.toLowerCase();

        if (searchedQuery.length < 2) {
            setSuggestions(null);
            setIsLoading(false);
            return;
        }

        let relations: Relation[] = [];
        let roles: Record<string, Role> = {};
        let grants: Record<string, Grant> = {};

        const resourceIdsResponse = await apiDispatch(searchResources({resourceName}));
        if (resourceIdsResponse.payload.error) {
            setSuggestions({error: resourceIdsResponse.payload.data});
            setIsLoading(false);
            return;
        }

        if (!resourceIdsResponse.payload.data.length) {
            setSuggestions({data: []});
            setIsLoading(false);
            return;
        }

        const relationsResponse = await apiDispatch(searchEntitlements({
            pageNumber: 1,
            pageSize: 250,
            sortDirection: SortDirection.ASC,
            requestBody: resourceIdsResponse.payload.data.map(({id}) => id),
        }));
        if (relationsResponse.payload.error) {
            setSuggestions({error: relationsResponse.payload.data});
            setIsLoading(false);
            return;
        } else {
            relations = relationsResponse.payload.data.items || [];
        }

        const roleIds = [...new Set(relations
            .filter(({roleId}) => roleId)
            .map(({roleId}) => roleId || ''),
        )];
        const rolesResponse = await apiDispatch(getRoleNamesByIds({requestBody: roleIds}));
        if (rolesResponse.payload.error) {
            setSuggestions({error: rolesResponse.payload.data});
            setIsLoading(false);
            return;
        } else {
            roles = rolesResponse.payload.data;
        }

        const grantIds = [...new Set(relations
            .map(({grantId}) => grantId),
        )];
        const grantsResponse = await apiDispatch(getGrantsByIds({requestBody: grantIds}));
        if (grantsResponse.payload.error) {
            setSuggestions({error: grantsResponse.payload.data});
            setIsLoading(false);
            return;
        } else {
            grants = grantsResponse.payload.data;
        }

        setSuggestions({data: mapRelationsToSuggestions({
            relations,
            roles,
            grants,
            maxResults: MAX_RESULTS,
        })});
        setIsLoading(false);
    }, 1000), []);

    useEffect(() => {
        loadSuggestedItems(query);
    }, [query, loadSuggestedItems]);

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;

        if (value === ' ' || value.endsWith('  ')) {
            return;
        }

        setIsLoading(true);
        setQuery(value);
    }, []);

    const blurHandler: FocusEventHandler<HTMLInputElement> = useCallback(() => {
        setQuery('');
        setSuggestions(null);
        setIsLoading(false);
    }, []);

    const handleItemSelect = useCallback((suggestion: SuggestionItem) => {
        if (!selectedSuggestions.includes(suggestion.id)) {
            onSearch(mapRelationsToPresetItems(suggestion.relations));
        }

        setQuery('');
        setSuggestions(null);
        setIsLoading(false);
    }, [onSearch, selectedSuggestions]);

    return (
        <WidthItem width={500}>
            <Autosuggest
                id={autosuggestId}
                value={query}
                maxLength={64}
                placeholder="Search for resources by keyword"
                onChange={handleChange}
                onBlur={blurHandler}
                onItemSelect={handleItemSelect}
                listboxProps={{
                    className: presetSuggestList,
                    id: listboxId,
                }}>
                <PresetSuggestResults
                    id={listboxId}
                    query={query}
                    isLoading={isLoading}
                    response={suggestions}
                    selectedSuggestions={selectedSuggestions}
                />
            </Autosuggest>
        </WidthItem>
    );
};

const mapRelationsToPresetItems = (relations: Relation[]): PresetRelationItem[] => relations.map(relation => ({
    ...(relation as PresetRelationItem),
    level: RelationLevel.CHILD,
}));
